<template>

  <nav class="navbar navbar-expand sticky-top bg-light shadow-sm py-0" tabindex="0">
    <div class="container-fluid">
      <RouterLink 
        class="navbar-brand py-0 nav-link" 
        to="/">
        <div class="px-3 py-0">
          <img
            src="../assets/EpicRoadTripPlannerLogo-SmallTransparent-125x50.png"
            width="125"
            height="50"
            alt="Epic Road Trip Planner Logo"
            loading="lazy"
          />
        </div>
      </RouterLink>

      <ul class="navbar-nav">
        <li class="nav-item dropdown">
          <DropdownMenuButton 
            class="nav-link dropdown-toggle" 
            data-bs-toggle="dropdown"
            aria-expanded="false"
            :dropdownMenu="dropdownMenu"
            >
          </DropdownMenuButton>
          <ul
            ref="dropdownMenu"
            class="dropdown-menu dropdown-menu-start">
            <li>
              <button 
                class="dropdown-item" 
                v-show="showSignIn" 
                @click="signIn">
                Sign in
              </button>
            </li>
            <li>
              <button 
                class="dropdown-item" 
                v-show="showSignUp" 
                @click="signUp">
                Sign up
              </button>
            </li>
            <li>
              <button 
                class="dropdown-item" 
                v-show="showSignOut" 
                @click="signOut">
                Sign out
              </button>
            </li>
            <li>
              <RouterLink 
                class="dropdown-item" 
                href="#"
                v-if="userState.isSignedIn && roadtripsService.roadtrips.value.length" 
                to="/roadtrips">
                My road trips
              </RouterLink>
            </li>
            <li>
              <RouterLink 
                class="dropdown-item" 
                href="#"
                v-if="showCreateNewRoadtrip" 
                to="/">
                New road trip
              </RouterLink>
            </li>
            <li>
              <RouterLink 
                class="dropdown-item" 
                href="#"
                v-if="userState?.isSignedIn && !userState.isAnonymous" 
                to="/profile">
                My profile
              </RouterLink>
            </li>
            <li>
              <RouterLink 
                class="dropdown-item" 
                href="#"
                v-if="showPricing" 
                to="/pricing">
                Subscription pricing
              </RouterLink>
            </li>
            <li>
              <RouterLink 
                class="dropdown-item" 
                href="#"
                v-if="showAdmin" 
                to="/admin">
                Admin console
              </RouterLink>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </nav>

  <ERTPSignInModal
    v-model:show="showSignInModal"
  />

  <ERTPSignUpModal
    v-model:show="showSignUpModal"
  />
  </template>

<script setup lang="ts">

import { Ref, computed, onMounted, ref } from 'vue';
import { UserState } from "../userState";
import { AuthControl } from '../authControl';
import { useRoute, useRouter } from 'vue-router';
import { RoadtripsService } from '../services/roadtripsService';
import { injectRequired } from '../utilities/injectRequired';
import { ProfilesService } from '../services/profilesService';
import { useCookieConsent } from '../composables/cookieConsent';
import DropdownMenuButton from '../components/buttons/DropdownMenuButton.vue';
import ERTPSignInModal from './modals/ERTPSignInModal.vue';
import ERTPSignUpModal from './modals/ERTPSignUpModal.vue';


const userState = injectRequired<Ref<UserState>>('userState');
const authControl = injectRequired<AuthControl>('authControl');
const roadtripsService = injectRequired<RoadtripsService>('roadtripsService');
const profilesService = injectRequired<ProfilesService>('profilesService')!;

const router = useRouter();
const route = useRoute();
const { accepted } = useCookieConsent();

const dropdownMenu = ref();

const showSignIn = computed(() => {
  if (!accepted.value) return false;
  if (userState.value.loading) return false;
  return !userState.value.isSignedIn || userState.value.isAnonymous;
});

const showSignUp = computed(() => {
  if (!accepted.value) return false;
  if (userState.value.loading) return false;
  return !userState.value.isSignedIn || userState.value.isAnonymous;
});


const showSignOut = computed(() => {
  if (userState.value.loading) return false;
  return userState.value.isSignedIn && !userState.value.isAnonymous
});

const showCreateNewRoadtrip = computed(() => {
  if (userState.value.loading) return false; // If we are loading, nothing else matters
  if (route.name == "home") return false; // Don't show the menu item on the page it's navigating to  
  if (!userState.value.isSignedIn) return true; // If we aren't signed in, we can always create (one) trip

  if(profilesService.isLoading.value) return false; // Can't create roadtrips while we're still getting the maximum
  if(!profilesService.profile.value) return false; // No profile, they only get to create one road trip, so don't show it
  if(!profilesService.profile.value.maxAllowedRoadtrips) return false;

  if(roadtripsService.isLoading.value) return false; // We don't know what to compare against (yet)
  // Similarly to the above sanity check, this SHOULDN'T happen, but if it does, deal with it ratioanlly
  if(!roadtripsService.roadtrips.value) { console.warn('Roadtrips are loaded, but empty - no way to determine how many we have.'); return false; }

  return (roadtripsService.roadtrips.value.length < profilesService.profile.value.maxAllowedRoadtrips);
})

const showPricing = computed(() => {
  if (userState.value.loading) return false; // If we are loading, nothing else matters
  if (profilesService.isLoading.value) return false; // Profile loading, not sure if we'll need to display it or now

  if (profilesService.profile.value
    && profilesService.profile.value.subscriptions
    && profilesService.profile.value.subscriptions.length) return false; // User has a subscription, don't sell past the close.

  return true;
});

const showAdmin = computed(() => {
  if (userState.value.loading) return false; // If we are loading, nothing else matters
  if (profilesService.isLoading.value) return false; // Profile loading, not sure if we'll need to display it or now

  if (!profilesService.profile.value) return false; // No profile; we don't know you.
  if (!profilesService.profile.value.roles) return false; // No roles, can't be an admin
 
  return profilesService.profile.value.roles.includes("admin");
})

onMounted(() => {
  authControl.startui();
});

async function signOut() {
  console.log("Signing out...");
  userState.value.loading++;
  await authControl.signOut();
  await router.replace({path: '/'});
  // Refresh the page, since there's a chance a user had data on "/" prior to logging out (typical
  // of the case where the user was anonymous, then upgraded, then logged out).
  await router.go(0);
}

const showSignInModal = ref<boolean>();
function signIn() {
  showSignInModal.value = true;
}

const showSignUpModal = ref<boolean>();
function signUp() {
  showSignUpModal.value = true;
}

</script>
